import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Search, Calendar } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface VincerBaseUser {
  name: string;
  email: string;
}

interface MentorData {
  uuid: string;
  vincer_base_user: VincerBaseUser;
}

interface MenteeData {
  uuid: string;
  vincer_base_user: VincerBaseUser;
}

interface ScheduledMeeting {
  uuid: string;
  mentor: {
    name: string;
    email: string;
  };
  mentee: {
    name: string;
    email: string;
  };
  scheduled_time: string;
  duration: number;
  status: 'scheduled' | 'completed' | 'cancelled';
  feedback?: any;
  meeting_link?: string;
  mentor_auth_token?: string;
  mentee_auth_token?: string;
}

export function ScheduledMeetingsList() {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState<ScheduledMeeting[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchMeetings() {
      try {
        const { data, error: fetchError } = await supabase
          .from('meeting')
          .select(`
            uuid,
            scheduled_time,
            duration,
            status,
            feedback,
            meeting_link,
            mentor_auth_token,
            mentee_auth_token,
            mentor:mentor_uuid (
              uuid,
              vincer_base_user (
                name,
                email
              )
            ),
            mentee:mentee_uuid (
              uuid,
              vincer_base_user (
                name,
                email
              )
            )
          `)
          .order('scheduled_time', { ascending: true });

        if (fetchError) throw fetchError;

        if (data) {
          const formattedMeetings = (data as unknown as any[]).map(meeting => ({
            uuid: meeting.uuid,
            mentor: {
              name: meeting.mentor.vincer_base_user.name,
              email: meeting.mentor.vincer_base_user.email,
            },
            mentee: {
              name: meeting.mentee.vincer_base_user.name,
              email: meeting.mentee.vincer_base_user.email,
            },
            scheduled_time: meeting.scheduled_time,
            duration: meeting.duration,
            status: meeting.status,
            feedback: meeting.feedback,
            meeting_link: meeting.meeting_link,
            mentor_auth_token: meeting.mentor_auth_token,
            mentee_auth_token: meeting.mentee_auth_token,
          }));
          setMeetings(formattedMeetings);
        }
      } catch (err) {
        console.error('Error fetching meetings:', err);
        setError('Failed to load scheduled meetings');
      } finally {
        setLoading(false);
      }
    }

    fetchMeetings();
  }, []);

  const filteredMeetings = meetings.filter(meeting => {
    const searchLower = searchTerm.toLowerCase();
    return (
      meeting.mentor.name.toLowerCase().includes(searchLower) ||
      meeting.mentor.email.toLowerCase().includes(searchLower) ||
      meeting.mentee.name.toLowerCase().includes(searchLower) ||
      meeting.mentee.email.toLowerCase().includes(searchLower)
    );
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">Scheduled Meetings</h1>
        <button
          onClick={() => navigate('/coordinator/meetings/new')}
          className="flex items-center gap-2 bg-[#4CAF50] text-white px-4 py-2 rounded-lg hover:bg-[#45a049] transition-colors"
        >
          <Calendar className="w-5 h-5" />
          <span>Schedule Meeting</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border mb-6">
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search meetings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ffcc32] focus:border-transparent"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffcc32] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading scheduled meetings...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-red-600">{error}</p>
          </div>
        ) : filteredMeetings.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-600">No scheduled meetings found</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredMeetings.map((meeting) => (
              <div
                key={meeting.uuid}
                className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => navigate(`/coordinator/meetings/${meeting.uuid}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-medium">
                        {meeting.mentor.name}
                      </h3>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                      <h3 className="font-medium">
                        {meeting.mentee.name}
                      </h3>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{meeting.mentor.email}</span>
                      <span>→</span>
                      <span>{meeting.mentee.email}</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDateTime(meeting.scheduled_time)} ({meeting.duration} minutes)
                    </div>
                    {meeting.feedback && (
                      <div className="text-sm text-gray-500 italic">
                        Feedback available
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(meeting.status)}`}>
                      {meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1)}
                    </span>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
} 